import { useOpenSubscriptionManagement } from 'features/user';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { ID } from 'shared/types/id';

import * as Styled from './subscription-management-links.style';

interface Props {
  stripeCustomerId?: ID;
  managementUrl?: string;
  isApple?: boolean;
}

export const SubscriptionManagementLinks: React.FC<Props> = ({
  stripeCustomerId,
  managementUrl: initialManagementUrl,
  isApple,
}) => {
  const { t } = useTranslation();
  const { submit: openSubscriptionManagement, isLoading } =
    useOpenSubscriptionManagement();

  const [managementUrl, setManagementUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!managementUrl) {
      return;
    }

    const timeout = setTimeout(
      () => setManagementUrl(null),
      5 * 60 * 1000, // session is 5 minutes valid
    );

    return () => clearTimeout(timeout);
  }, [managementUrl]);

  const onManageSubscription = async () => {
    if (initialManagementUrl) {
      window.open(initialManagementUrl, '_blank');
      return;
    }

    if (!stripeCustomerId) {
      return;
    }

    const url = await openSubscriptionManagement(stripeCustomerId);

    if (url) {
      setManagementUrl(url);
    }
  };

  return managementUrl ? (
    <Styled.Container>
      <Button
        as="a"
        // @ts-ignore
        href={managementUrl}
        target="_blank"
        size={Sizes.Small}
        variant={Variants.Outlined}
        color={Colors.Inherit}
      >
        {t(
          'forms.user-settings.sections.subscriptions.premium.button.update.label',
        )}
      </Button>

      <Button
        as="a"
        // @ts-ignore
        href={managementUrl}
        target="_blank"
        size={Sizes.Small}
        variant={Variants.Outlined}
        color={Colors.Error}
      >
        {t(
          'forms.user-settings.sections.subscriptions.premium.button.cancel.label',
        )}
      </Button>
    </Styled.Container>
  ) : (
    <Button
      onClick={onManageSubscription}
      size={Sizes.Small}
      variant={Variants.Outlined}
      color={Colors.Inherit}
      disabled={isLoading}
    >
      {t(
        isApple
          ? 'forms.user-settings.sections.subscriptions.premium.button.manage-in-ios.label'
          : 'forms.user-settings.sections.subscriptions.premium.button.label',
      )}
      {isLoading && <Styled.ButtonLoader color="primary" size={12} />}
    </Button>
  );
};
